import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { useLocationAutocomplete } from 'src/hooks/useLocationAutocomplete'
import { operacionOptions as OPERACION_OPTIONS, propiedadOptions as PROPIEDAD_OPTIONS, SHOW_IA_SEARCH } from './classicSearch.constants'

interface SearchModalProps {
	open: boolean
	onClose: () => void
	tipoOperacionFamilia?: string
	tipoPropiedadFamilia?: string
}

/* ─── Constants ─────────────────────────────────────── */

const IA_EXAMPLES = [
	'Departamento en Ñuñoa, 2 dormitorios, hasta 5.000 UF',
	'Casa en arriendo en La Reina con patio',
	'Depto cerca de metro en Providencia',
]

// Búsqueda IA oculta este release (SHOW_IA_SEARCH) — fuerza tab clásica sin importar la env var.
const DEFAULT_TAB: 'clasica' | 'ia' =
	!SHOW_IA_SEARCH || process.env.NEXT_PUBLIC_DEFAULT_SEARCH_MODE === 'normal' ? 'clasica' : 'ia'

/* ─── Icons ─────────────────────────────────────── */

const XIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M18 6 6 18" /><path d="m6 6 12 12" />
	</svg>
)

const SparklesIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
		<path d="M20 2v4" /><path d="M22 4h-4" /><circle cx="4" cy="20" r="2" />
	</svg>
)

const SearchIconSm = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="m21 21-4.34-4.34" /><circle cx="11" cy="11" r="8" />
	</svg>
)

const PinIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, color: '#999' }}>
		<path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" /><circle cx="12" cy="10" r="3" />
	</svg>
)

/* ─── Component ─────────────────────────────────────── */

export const SearchModal = ({ open, onClose, tipoOperacionFamilia = '', tipoPropiedadFamilia = '' }: SearchModalProps) => {
	const [tab, setTab] = useState<'clasica' | 'ia'>(DEFAULT_TAB)

	const initialOp = OPERACION_OPTIONS.find(o => o.value === tipoOperacionFamilia)?.value ?? 'venta'
	const initialTipo = PROPIEDAD_OPTIONS.find(p => p.value === tipoPropiedadFamilia)?.value ?? 'departamento'
	const [operacion, setOperacion] = useState(initialOp)
	const [tipoPropiedad, setTipoPropiedad] = useState(initialTipo)

	// Ubicación autocomplete state
	const ubic = useLocationAutocomplete()
	const [ubicError, setUbicError] = useState<string | null>(null)

	// IA search state
	const [texto, setTexto] = useState('')
	const [iaCargando, setIaCargando] = useState(false)
	const [iaError, setIaError] = useState<string | null>(null)

	// Precarga los inputs con el resultado guardado por el buscador anterior (home, otra lista, etc.)
	useEffect(() => {
		try {
			const rawAddress = localStorage.getItem('toctocSearchAddress')
			const savedAddress = rawAddress ? JSON.parse(rawAddress) : null
			if (savedAddress?.label) ubic.restore(savedAddress)

			const rawIa = localStorage.getItem('toctocSearchIa')
			const iaLabel = rawIa ? JSON.parse(rawIa)?.label : null
			if (iaLabel) setTexto(iaLabel)
		} catch {
			// localStorage no disponible o con contenido corrupto: se ignora
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	if (!open) return null

	/* ─── Classic search ─── */
	const handleBuscarClasico = () => {
		const { url, error } = ubic.buildUrl({ operacion, tipoPropiedad })
		if (error) {
			setUbicError(error)
			return
		}
		window.location.href = url as string
	}

	/* ─── IA search ─── */
	const handleBuscarIA = async () => {
		if (!texto.trim() || iaCargando) return
		setIaCargando(true)
		setIaError(null)
		try {
			const baseApi = process.env.NEXT_PUBLIC_HOME_BASE_API
			const tokenRes = await fetch(`${baseApi}/search/ia-token`)
			const { token } = await tokenRes.json()
			const res = await fetch(`${baseApi}/search/ia`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ texto, token }),
			})
			const data = await res.json()
			if (data.exito && data.url) {
				try {
					localStorage.setItem('toctocSearchIa', JSON.stringify(data))
				} catch {
					// localStorage no disponible: se ignora
				}
				window.location.href = data.url
			} else {
				setIaError(data.message || 'Error al procesar la búsqueda.')
				setIaCargando(false)
			}
		} catch {
			setIaError('Error al conectar con el servicio de búsqueda.')
			setIaCargando(false)
		}
	}

	return (
		<Overlay role="dialog" aria-modal="true" aria-label="Refinar búsqueda">
			{/* Header */}
			<ModalHeader>
				<ModalTitle>Refinar búsqueda</ModalTitle>
				<CloseBtn type="button" onClick={onClose} aria-label="Cerrar buscador">
					<XIcon />
				</CloseBtn>
			</ModalHeader>

			{/* Tabs — oculto este release (SHOW_IA_SEARCH en classicSearch.constants) */}
			{SHOW_IA_SEARCH && (
				<TabBar>
					<Tab
						role="tab"
						aria-selected={tab === 'clasica'}
						data-active={tab === 'clasica' ? 'true' : undefined}
						onClick={() => setTab('clasica')}
					>
						Búsqueda clásica
					</Tab>
					<Tab
						role="tab"
						aria-selected={tab === 'ia'}
						data-active={tab === 'ia' ? 'true' : undefined}
						onClick={() => setTab('ia')}
					>
						<SparklesIcon />
						Búsqueda IA
						<BetaBadge>Beta</BetaBadge>
					</Tab>
				</TabBar>
			)}

			{/* Scrollable body */}
			<ModalBody>
				{tab === 'clasica' ? (
					<ContentPad>
						{/* Operación */}
						<Section>
							<SectionLabel>Operación</SectionLabel>
							<PillGroup>
								{OPERACION_OPTIONS.map(o => (
									<Pill key={o.value} data-active={operacion === o.value ? 'true' : undefined} onClick={() => setOperacion(o.value)}>
										{o.label}
									</Pill>
								))}
							</PillGroup>
						</Section>

						{/* Tipo de propiedad */}
						<Section>
							<SectionLabel>Tipo de propiedad</SectionLabel>
							<PillGroup>
								{PROPIEDAD_OPTIONS.map(p => (
									<Pill key={p.value} data-active={tipoPropiedad === p.value ? 'true' : undefined} onClick={() => setTipoPropiedad(p.value)}>
										{p.label}
									</Pill>
								))}
							</PillGroup>
						</Section>

						{/* Ubicación autocomplete */}
						<Section>
							<SectionLabel>Ubicación</SectionLabel>
							<UbicWrapper>
								<UbicInputRow>
									<SearchIconSm />
									<UbicInput
										type="text"
										placeholder="Busca una comuna o región..."
										value={ubic.query}
										onChange={e => { setUbicError(null); ubic.setQuery(e.target.value) }}
										autoComplete="off"
										aria-label="Ubicación"
									/>
									{ubic.loading && <UbicSpinner />}
									{ubic.query && !ubic.loading && (
										<ClearBtn
											type="button"
											aria-label="Limpiar ubicación"
											onClick={() => ubic.clear()}
										>
											<XIcon />
										</ClearBtn>
									)}
								</UbicInputRow>
								{ubic.suggestions.length > 0 && (
									<SuggestionsList role="listbox">
										{ubic.suggestions.map((s, i) => (
											<SuggestionItem
												key={`${s.label}-${i}`}
												role="option"
												onClick={() => ubic.select(s)}
											>
												<PinIcon />
												<SuggestionText>
													<strong>{s.label}</strong>
													{s.sublabel && <span>{s.sublabel}</span>}
												</SuggestionText>
											</SuggestionItem>
										))}
									</SuggestionsList>
								)}
							</UbicWrapper>
							{ubicError && ubic.suggestions.length === 0 && <ErrorMsg>{ubicError}</ErrorMsg>}
						</Section>

						<BuscarBtn type="button" onClick={handleBuscarClasico}>
							<SearchIconSm />
							Buscar
						</BuscarBtn>
					</ContentPad>
				) : (
					<ContentPad style={{ gap: 16 }}>
						{/* IA search */}
						<Section>
							<SectionLabel style={{ fontWeight: 600, color: '#666' }}>
								Describe en lenguaje natural lo que buscas:
							</SectionLabel>
							{iaCargando ? (
								<LoadingBox>
									<SearchIconSm />
									<LoadingText>
										<LoadingLabel>Buscando propiedades compatibles...</LoadingLabel>
										<LoadingQuery>&ldquo;{texto}&rdquo;</LoadingQuery>
									</LoadingText>
								</LoadingBox>
							) : (
								<IATextarea
									placeholder='Ej: "Depto cerca de metro en Providencia"'
									rows={3}
									value={texto}
									onChange={e => setTexto(e.target.value)}
								/>
							)}
						</Section>

						<ExamplesGroup>
							{IA_EXAMPLES.map(ex => (
								<ExampleBtn key={ex} type="button" onClick={() => setTexto(ex)}>
									{ex}
								</ExampleBtn>
							))}
						</ExamplesGroup>

						{iaError && <ErrorMsg>{iaError}</ErrorMsg>}

						<BuscarBtn
							type="button"
							disabled={!texto.trim() || iaCargando}
							onClick={handleBuscarIA}
						>
							<SearchIconSm />
							{iaCargando ? 'Buscando...' : 'Buscar'}
						</BuscarBtn>
					</ContentPad>
				)}
			</ModalBody>
		</Overlay>
	)
}

/* ─── Styled Components ─────────────────────────────────────── */

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 1500;
	background: #ffffff;
	display: flex;
	flex-direction: column;
	padding-top: env(safe-area-inset-top);
	padding-bottom: env(safe-area-inset-bottom);
`

const ModalHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 16px;
	border-bottom: 1px solid #e5e5e5;
	flex-shrink: 0;
`

const ModalTitle = styled.h2`
	font-size: 16px;
	font-weight: 800;
	color: #343a40;
	margin: 0;
`

const CloseBtn = styled.button`
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: none;
	background: #f5f5f5;
	border-radius: 8px;
	cursor: pointer;
	color: #666;
	flex-shrink: 0;

	&:active { background: #e8e8e8; }
`

const TabBar = styled.div`
	display: flex;
	border-bottom: 1px solid #e5e5e5;
	flex-shrink: 0;
`

const Tab = styled.button`
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	padding: 12px 0;
	font-size: 13px;
	font-weight: 700;
	border: none;
	border-bottom: 2px solid transparent;
	background: none;
	color: #666;
	cursor: pointer;
	font-family: inherit;
	transition: color 0.15s;

	&[data-active="true"] {
		border-bottom-color: #3200c1;
		color: #3200c1;
	}
`

const BetaBadge = styled.span`
	font-size: 9px;
	font-weight: 700;
	padding: 2px 5px;
	border-radius: 4px;
	background: #e8fffb;
	color: #0e7490;
`

const ModalBody = styled.div`
	flex: 1;
	overflow-y: auto;
	overscroll-behavior: contain;
`

const ContentPad = styled.div`
	padding: 20px 16px;
	display: flex;
	flex-direction: column;
	gap: 20px;
`

const Section = styled.div``

const SectionLabel = styled.p`
	font-size: 12px;
	font-weight: 700;
	color: #343a40;
	margin: 0 0 8px;
`

const PillGroup = styled.div`
	display: flex;
	gap: 6px;
	flex-wrap: wrap;
`

const Pill = styled.button`
	padding: 7px 14px;
	border-radius: 20px;
	border: 1.5px solid #e5e5e5;
	background: #fff;
	color: #555;
	font-size: 13px;
	font-weight: 700;
	cursor: pointer;
	font-family: inherit;
	min-height: 36px;
	transition: 0.12s;

	&[data-active="true"] {
		border-color: #3200c1;
		background: #eaf2fc;
		color: #3200c1;
	}
`

/* ─── Ubicación Autocomplete ─────────────────────── */

const UbicWrapper = styled.div`
	position: relative;
`

const UbicInputRow = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 0 12px;
	border-radius: 10px;
	border: 1.5px solid #e5e5e5;
	background: #fff;
	min-height: 44px;
	transition: border-color 0.12s;
	color: #999;

	&:focus-within {
		border-color: #3200c1;
		color: #3200c1;
	}
`

const UbicInput = styled.input`
	flex: 1;
	border: none;
	outline: none;
	background: none;
	font-size: 14px;
	color: #343a40;
	font-family: Nunito, sans-serif;
	padding: 10px 0;

	&::placeholder { color: #bbb; }
`

const UbicSpinner = styled.span`
	width: 16px;
	height: 16px;
	border: 2px solid #e5e5e5;
	border-top-color: #3200c1;
	border-radius: 50%;
	flex-shrink: 0;
	animation: spin 0.7s linear infinite;

	@keyframes spin { to { transform: rotate(360deg); } }
`

const ClearBtn = styled.button`
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
	color: #bbb;
	display: flex;
	align-items: center;
	flex-shrink: 0;

	&:hover { color: #666; }
`

const SuggestionsList = styled.ul`
	position: absolute;
	top: calc(100% + 4px);
	left: 0;
	right: 0;
	margin: 0;
	padding: 6px 0;
	background: #fff;
	border: 1.5px solid #e5e5e5;
	border-radius: 10px;
	box-shadow: 0 4px 16px rgba(0,0,0,0.10);
	z-index: 10;
	list-style: none;
	overflow: hidden;
`

const SuggestionItem = styled.li`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 11px 14px;
	font-size: 14px;
	color: #343a40;
	cursor: pointer;
	font-family: Nunito, sans-serif;

	&:hover { background: #f5f5f5; }
	&:active { background: #eaf2fc; }
`

const SuggestionText = styled.span`
	display: flex;
	flex-direction: column;
	gap: 1px;
	min-width: 0;

	strong {
		font-weight: 700;
	}

	span {
		font-size: 12px;
		color: #999;
	}
`

/* ─── IA ─────────────────────── */

const IATextarea = styled.textarea`
	width: 100%;
	padding: 12px 14px;
	border-radius: 12px;
	border: 1.5px solid #e5e5e5;
	background: #fff;
	color: #343a40;
	font-size: 14px;
	font-family: Nunito, sans-serif;
	outline: none;
	resize: none;
	box-sizing: border-box;
	line-height: 1.5;

	&::placeholder { color: #999; }
	&:focus { border-color: #3200c1; }
`

const LoadingBox = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	min-height: 44px;
	padding: 12px 14px;
	border-radius: 12px;
	border: 1.5px solid #C1B2EC;
	background: #F3F0FE;
	box-sizing: border-box;
	color: #3200c1;
`

const LoadingText = styled.p`
	margin: 0;
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 4px;
`

const LoadingLabel = styled.span`
	font-size: 14px;
	font-weight: 700;
	font-family: Nunito, sans-serif;
	color: #3200c1;
`

const LoadingQuery = styled.span`
	font-size: 14px;
	font-family: Nunito, sans-serif;
	color: #8A8A8A;
`

const ExamplesGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`

const ExampleBtn = styled.button`
	text-align: left;
	font-size: 13px;
	padding: 10px 14px;
	border-radius: 10px;
	border: 1px solid #e5e5e5;
	background: #f9f9f9;
	color: #343a40;
	cursor: pointer;
	font-family: inherit;
	min-height: 44px;
	line-height: 1.4;

	&:active { background: #f0f0f0; }
`

const ErrorMsg = styled.p`
	font-size: 13px;
	color: #e63946;
	margin: 0;
	padding: 10px 14px;
	background: #fff3f4;
	border: 1px solid #e63946;
	border-radius: 8px;
`

const BuscarBtn = styled.button`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 14px;
	border-radius: 12px;
	border: none;
	background: #3200c1;
	color: #fff;
	font-size: 15px;
	font-weight: 800;
	cursor: pointer;
	font-family: inherit;
	transition: background 0.15s;
	min-height: 52px;

	&:hover:not(:disabled) { background: #2600a3; }
	&:disabled {
		background: #c5c5c5;
		cursor: not-allowed;
	}
`
